<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;
    import { __, resizeObserver } from '@oat-sa-private/ui-core';
    import FlyoutMenu from '../flyoutMenu/FlyoutMenu.svelte';

    /**
     * @typedef {Object} BreadcrumbItem
     * @property {string} label
     * @property {string} [href]
     */

    /**
     * @typedef {Object} Breadcrumbs
     * @property {BreadcrumbItem[]} preFlyoutItems
     * @property {BreadcrumbItem[]} postFlyoutItems
     * @property {BreadcrumbItem[]} flyoutItems
     */

    export let ariaLabel = __('breadcrumb');
    export let items = [];
    export let separator = '/';

    let resizeHelper = null;
    let flyoutToggle = null;
    let isFlyoutOpen = false;
    let containerWidth = 0;
    let flyoutBreadcrumbWidth = 0;

    const itemsWidths = new WeakMap();

    $: breadcrumbs = calculateBreadcrumbItems(items, flyoutBreadcrumbWidth, containerWidth);

    /**
     * @param {ResizeObserverEntry} entry
     */
    function containerResizeObserverCallback(entry) {
        containerWidth = entry.borderBoxSize[0].inlineSize;
    }

    /**
     * @param {ResizeObserverEntry} entry
     */
    function flyoutBreadcrumbResizeObserverCallback(entry) {
        flyoutBreadcrumbWidth = entry.borderBoxSize[0].inlineSize;
    }

    /**
     * @param {BreadcrumbItem[]} _items
     * @param {number} _flyoutBreadcrumbWidth
     * @param {number} _containerWidth
     * @returns {Breadcrumbs}
     */
    function calculateBreadcrumbItems(_items = [], _flyoutBreadcrumbWidth = 0, _containerWidth = 0) {
        if (_items.length === 0) {
            return {
                preFlyoutItems: [],
                postFlyoutItems: [],
                flyoutItems: []
            };
        }
        const keyedItems = _items.map((item, index) => ({
            ...item,
            key: index,
            width: itemsWidths.get(item) ?? 0
        }));
        const preFlyoutItems = [];
        const postFlyoutItems = [];
        let totalVisibleItemsWidth = _flyoutBreadcrumbWidth;

        const lastItem = keyedItems.pop();
        postFlyoutItems.push(lastItem);
        const lastItemWidth = lastItem.width;
        totalVisibleItemsWidth += lastItemWidth;

        let i = 0;
        while (keyedItems.length > 0 && totalVisibleItemsWidth <= _containerWidth) {
            const isPreflyoutItem = i % 2 === 0;
            const item = isPreflyoutItem ? keyedItems.shift() : keyedItems.pop();
            const itemWidth = item.width;
            if (totalVisibleItemsWidth + itemWidth <= _containerWidth) {
                if (isPreflyoutItem) {
                    preFlyoutItems.push(item);
                } else {
                    postFlyoutItems.unshift(item);
                }
                totalVisibleItemsWidth += itemWidth;
                i++;
            } else {
                if (isPreflyoutItem) {
                    keyedItems.unshift(item);
                } else {
                    keyedItems.push(item);
                }
                break;
            }
        }

        return {
            preFlyoutItems,
            postFlyoutItems,
            flyoutItems: keyedItems
        };
    }

    /**
     * Sets breadcrumb item width in the map
     * @param {BreadcrumbItem} item
     * @returns {(entry: ResizeObserverEntry) => void}
     */
    function createItemResizeObserverCallback(item) {
        return entry => {
            itemsWidths.set(item, entry.borderBoxSize[0].inlineSize);
        };
    }

    /**
     * Handle flyout item action
     * @param {Event} evt
     */
    function handleFlyoutAction(evt) {
        const clickedItem = breadcrumbs.flyoutItems.find(item => evt.detail.key === item.key);
        if (typeof clickedItem.onClick === 'function') {
            clickedItem.onClick();
        } else {
            const contentLink = resizeHelper.querySelector(`li:nth-child(${clickedItem.key + 2}) .content a`);
            contentLink?.click();
        }
    }
</script>

<style>
    .breadcrumb {
        &.resize-helper {
            visibility: hidden;
            max-width: 0;
        }

        display: flex;
        margin: 0;
        padding: 0;
        width: 100%;
        list-style: none;

        & li {
            display: flex;
            align-items: center;
        }

        & li + li {
            padding-inline-start: 1rem;
        }

        &:not(.visually-hidden) li:last-child {
            min-width: 0;
            flex-shrink: 1;

            & .content {
                flex-shrink: 1;
            }
        }

        & li:last-child {
            & .separator {
                display: none;
            }
        }
    }

    .content {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex-shrink: 0;
        min-width: 0;
        padding: var(--border-medium);

        /* Fix to not cut borders */
        & > :global-nested(*) {
            @add-mixin outline-focus 0rem;
        }
    }

    .flyout-toggle {
        padding: 0;
        border: unset;
        background: unset;
        &:not(:hover) {
            color: inherit;
        }
    }

    .separator {
        margin-inline-start: 1rem;
    }
</style>

<ul class="breadcrumb resize-helper visually-hidden" aria-hidden="true" aria-label={ariaLabel} bind:this={resizeHelper}>
    <li use:resizeObserver={{ callback: flyoutBreadcrumbResizeObserverCallback }}>
        <span class="content">
            <button class="ui-link text-md flyout-toggle">...</button>
        </span>
        <span class="separator">
            <slot name="separator">{separator}</slot>
        </span>
    </li>
    {#each items as item}
        <li use:resizeObserver={{ callback: createItemResizeObserverCallback(item) }}>
            <span class="content">
                <slot {item} />
            </span>
            <span class="separator">
                <slot name="separator">{separator}</slot>
            </span>
        </li>
    {/each}
</ul>
<ul class="breadcrumb" aria-label={ariaLabel} use:resizeObserver={{ callback: containerResizeObserverCallback }}>
    {#each breadcrumbs.preFlyoutItems as item}
        <li>
            <span class="content">
                <slot {item} />
            </span>
            <span class="separator">
                <slot name="separator">{separator}</slot>
            </span>
        </li>
    {/each}
    {#if breadcrumbs.flyoutItems.length}
        <li>
            <span class="content">
                <button
                    class="ui-link text-md flyout-toggle"
                    bind:this={flyoutToggle}
                    on:click={() => {
                        isFlyoutOpen = true;
                    }}
                    title={__.p('%d more item', '%d more items', breadcrumbs.flyoutItems.length)}
                    aria-label={__.p('%d more item', '%d more items', breadcrumbs.flyoutItems.length)}
                >...</button>
            </span>
            <span class="separator">
                <slot name="separator">{separator}</slot>
            </span>
            <FlyoutMenu bind:open={isFlyoutOpen} caller={flyoutToggle} items={breadcrumbs.flyoutItems} on:action={handleFlyoutAction} />
        </li>
    {/if}
    {#each breadcrumbs.postFlyoutItems as item, index}
        <li aria-current={breadcrumbs.postFlyoutItems.length === index + 1 ? 'location' : void 0}>
            <span class="content">
                <slot {item} />
            </span>
            <span class="separator">
                <slot name="separator">{separator}</slot>
            </span>
        </li>
    {/each}
</ul>
